import { date, number, object, string } from 'yup'
import { compareAsc, parse } from 'date-fns'
import { isNil } from 'lodash'
import { isPaymentMethodRequiredForTeamPerks } from '../admin/adminUtils'

export const PROGRAM_DIALOG_HEIGHT = '520px'

export const STEPS = Object.freeze({
  PROGRAM_USAGE_TYPE: 0,
  PROGRAM_AMOUNT: 1,
  PROGRAM_MEMBERS: 2,
  PROGRAM_DETAILS: 3,
  PROGRAM_ADD_PAYMENT: 4,
  PROGRAM_COMPLETE: 5,
})

export const getTotalDottedSteps = (hasPaymentMethod, teamPaymentCollectionMethod) =>
  isPaymentMethodRequiredForTeamPerks(hasPaymentMethod, teamPaymentCollectionMethod) ? 5 : 4

export const getStepDetails = (currentStep, hasPaymentMethod, teamPaymentCollectionMethod) => {
  switch (currentStep) {
    case STEPS.PROGRAM_USAGE_TYPE:
      return {
        step: 1,
        title: 'Select Subsidy Type',
        nextStep: STEPS.PROGRAM_AMOUNT,
        previousStep: undefined,
      }
    case STEPS.PROGRAM_AMOUNT:
      return {
        step: 2,
        title: 'Set Amount',
        nextStep: STEPS.PROGRAM_MEMBERS,
        previousStep: STEPS.PROGRAM_USAGE_TYPE,
        validationSchema: object().shape({
          discountAmountInPennies: number().min(1, 'Amount must be greater than zero.'),
          schedule: object().test('schedule', 'A minimum of one day must be selected.', function() {
            const usageType = this.options.parent.usageType
            const schedule = this.options.parent.schedule
            return (
              (usageType === 'DAILY' && Object.values(schedule).some(day => day === true)) || // at least one day must be TRUE if DAILY is selected
              usageType === 'WEEKLY' ||
              usageType === 'MONTHLY'
            )
          }),
        }),
      }
    case STEPS.PROGRAM_MEMBERS:
      return {
        step: 3,
        title: 'Add Members',
        nextStep: STEPS.PROGRAM_DETAILS,
        previousStep: STEPS.PROGRAM_AMOUNT,
        validationSchema: object().shape({}),
      }
    case STEPS.PROGRAM_DETAILS:
      return {
        step: 4,
        title: 'Subsidy Details',
        nextStep: isPaymentMethodRequiredForTeamPerks(hasPaymentMethod, teamPaymentCollectionMethod)
          ? STEPS.PROGRAM_ADD_PAYMENT
          : STEPS.PROGRAM_COMPLETE,
        previousStep: STEPS.PROGRAM_MEMBERS,
        validationSchema: object().shape({
          name: string().required('Subsidy name is required.'),
          startDate: date().test('startDate', 'Start date cannot be before today.', function() {
            const today = new Date().setHours(0, 0, 0, 0)
            const startDate = parse(this.options.parent.startDate)
            return !isNil(startDate) && compareAsc(startDate, today) >= 0
          }),
          endDate: date().test('endDate', 'End date cannot be before start date.', function() {
            const startDate = this.options.parent.startDate
            const endDate = this.options.parent.endDate
            return isNil(endDate) || compareAsc(endDate, startDate) >= 0
          }),
        }),
      }
    case STEPS.PROGRAM_ADD_PAYMENT:
      return {
        step: 5,
        title: 'Add Payment Method',
        nextStep: STEPS.PROGRAM_COMPLETE,
        previousStep: STEPS.PROGRAM_DETAILS,
        validationSchema: object().shape({}),
      }
    default:
      return {}
  }
}

export const getProgramUsageTypeDescription = usageType => {
  switch (usageType) {
    case 'DAILY':
      return 'Give your team members a set amount for lunch each day.'
    case 'WEEKLY':
      return 'Give your team members a set amount they can spend each week.'
    case 'MONTHLY':
      return 'Give your team members a set amount they can spend each month.'
    default:
      return 'Give your team members a set amount they can spend over the course of this time period.'
  }
}

export const getProgramAmountDescription = usageType => {
  switch (usageType) {
    case 'DAILY':
      return 'How much will each team member receive each day for lunch?'
    case 'WEEKLY':
      return 'How much will each team member receive each week for lunch?'
    case 'MONTHLY':
      return 'How much will each team member receive each month for lunch?'
    default:
      return 'How much will each team member receive over the selected time period for lunch?'
  }
}

export const DAYS = [
  {
    value: 'mondayUsage',
    label: 'Monday',
  },
  {
    value: 'tuesdayUsage',
    label: 'Tuesday',
  },
  {
    value: 'wednesdayUsage',
    label: 'Wednesday',
  },
  {
    value: 'thursdayUsage',
    label: 'Thursday',
  },
  {
    value: 'fridayUsage',
    label: 'Friday',
  },
]
